import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const CookiesPolicyPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: 'Política de Cookies',
      lastUpdated: 'Última actualización: Enero 2026',
      backHome: 'Volver al inicio',
      sections: [
        {
          title: '1. ¿Qué son las Cookies?',
          content: `Las cookies son pequeños archivos de texto que los sitios web almacenan en su dispositivo (ordenador, tablet o móvil) cuando los visita. Estas cookies permiten que el sitio web recuerde sus acciones y preferencias durante un período de tiempo.`
        },
        {
          title: '2. Cookies que Utilizamos',
          content: `En Domotics utilizamos únicamente cookies técnicas esenciales para el funcionamiento básico del sitio web:

• Cookies de sesión: Necesarias para la navegación y el funcionamiento del sitio. Se eliminan automáticamente al cerrar el navegador.

• Cookies de preferencias: Almacenan su preferencia de idioma (español/inglés) para mejorar su experiencia de usuario.

NO utilizamos:
• Cookies de seguimiento o análisis
• Cookies publicitarias
• Cookies de terceros para marketing`
        },
        {
          title: '3. Cookies Técnicas Utilizadas',
          content: `Detalle de las cookies que utilizamos:

| Nombre | Propósito | Duración |
|--------|-----------|----------|
| language | Guarda su preferencia de idioma | 1 año |

Estas cookies son estrictamente necesarias y no requieren su consentimiento según la normativa vigente, ya que son esenciales para el funcionamiento del sitio.`
        },
        {
          title: '4. Control de Cookies',
          content: `Puede controlar y/o eliminar las cookies según desee. Puede eliminar todas las cookies que ya están en su ordenador y configurar la mayoría de los navegadores para que no las acepten.

Para gestionar las cookies en su navegador:

• Chrome: Configuración > Privacidad y seguridad > Cookies
• Firefox: Opciones > Privacidad y seguridad > Cookies
• Safari: Preferencias > Privacidad > Cookies
• Edge: Configuración > Cookies y permisos del sitio

Si desactiva las cookies, es posible que algunas funciones del sitio web no funcionen correctamente.`
        },
        {
          title: '5. Cookies de Terceros',
          content: `Actualmente, este sitio web NO utiliza cookies de terceros. No empleamos servicios de análisis como Google Analytics ni herramientas de publicidad que instalen cookies en su dispositivo.

Si en el futuro incorporamos algún servicio que utilice cookies de terceros, actualizaremos esta política y le informaremos debidamente.`
        },
        {
          title: '6. Actualizaciones de esta Política',
          content: `Podemos actualizar esta política de cookies ocasionalmente para reflejar cambios en las cookies que utilizamos o por otras razones operativas, legales o regulatorias.

Le recomendamos visitar esta página regularmente para mantenerse informado sobre nuestro uso de cookies.`
        },
        {
          title: '7. Contacto',
          content: `Si tiene preguntas sobre nuestra política de cookies, puede contactarnos en:

Email: admin@domodomotics.com
Web: www.domodomotics.es`
        }
      ]
    },
    en: {
      title: 'Cookies Policy',
      lastUpdated: 'Last updated: January 2026',
      backHome: 'Back to home',
      sections: [
        {
          title: '1. What are Cookies?',
          content: `Cookies are small text files that websites store on your device (computer, tablet or mobile) when you visit them. These cookies allow the website to remember your actions and preferences over a period of time.`
        },
        {
          title: '2. Cookies We Use',
          content: `At Domotics we only use essential technical cookies for the basic operation of the website:

• Session cookies: Necessary for navigation and site operation. They are automatically deleted when you close the browser.

• Preference cookies: Store your language preference (Spanish/English) to improve your user experience.

We DO NOT use:
• Tracking or analytics cookies
• Advertising cookies
• Third-party marketing cookies`
        },
        {
          title: '3. Technical Cookies Used',
          content: `Details of the cookies we use:

| Name | Purpose | Duration |
|------|---------|----------|
| language | Stores your language preference | 1 year |

These cookies are strictly necessary and do not require your consent under current regulations, as they are essential for the operation of the site.`
        },
        {
          title: '4. Cookie Control',
          content: `You can control and/or delete cookies as you wish. You can delete all cookies that are already on your computer and set most browsers not to accept them.

To manage cookies in your browser:

• Chrome: Settings > Privacy and security > Cookies
• Firefox: Options > Privacy and security > Cookies
• Safari: Preferences > Privacy > Cookies
• Edge: Settings > Cookies and site permissions

If you disable cookies, some website features may not work properly.`
        },
        {
          title: '5. Third-Party Cookies',
          content: `Currently, this website does NOT use third-party cookies. We do not use analytics services like Google Analytics or advertising tools that install cookies on your device.

If we incorporate any service that uses third-party cookies in the future, we will update this policy and inform you accordingly.`
        },
        {
          title: '6. Updates to this Policy',
          content: `We may update this cookies policy occasionally to reflect changes in the cookies we use or for other operational, legal or regulatory reasons.

We recommend visiting this page regularly to stay informed about our use of cookies.`
        },
        {
          title: '7. Contact',
          content: `If you have questions about our cookies policy, you can contact us at:

Email: admin@domodomotics.com
Web: www.domodomotics.es`
        }
      ]
    }
  };

  const t = content[language] || content.es;

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-brand-light hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={20} />
          {t.backHome}
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">{t.title}</h1>
          <p className="text-white/40">{t.lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {t.sections.map((section, index) => (
            <div key={index} className="glass-card p-8 rounded-2xl">
              <h2 className="font-heading text-xl font-medium text-white mb-4">{section.title}</h2>
              <p className="text-white/55 leading-relaxed whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicyPage;
